import React, { useMemo, useState } from 'react';
import { Button, Card, Segmented, Tag } from 'antd';
import {
  AppstoreOutlined,
  BarsOutlined,
  DownloadOutlined,
  FileTextOutlined,
  SendOutlined,
} from '@ant-design/icons';
import {
  submitOrderGenieErpOrder,
  searchOrderGenieErpShipToAddresses,
  updateOrderGenieCart,
  updateOrderGenieSummary,
} from '../../services/orderXApi';
import {
  buildCustomerShipToPayload,
  buildProductImageUrl,
  decodeHtmlEntities,
  exportReviewToExcel,
  getErpMatchBadge,
  getMatchAiBadge,
  getShippingMethodLabel,
  getPriceDivergenceBadge,
  getUomMismatchBadge,
  resolveEffectiveLinePrice,
} from '../../pages/orderXV4Utils';
import { getAuthUserFromStorage } from '../../utils/authHeaders';
import { scopedKey, getActiveAccountId } from '../../utils/accountSession';
import {
  CARD_STYLES, ExtractedFieldsBlock, PricingPanel, ProductImage, SimpleExtractedFields,
} from './LineItemPriceDisplay';
import CustomerShipToContactCards from './CustomerShipToContactCards';

const FIELD_LABELS = {
  customer: 'Customer',
  ship_to: 'Ship To',
  contact: 'Contact',
  shipping_method: 'Shipping Method',
};

const ORDER_TYPE_TOGGLE_OPTIONS = [
  { value: 'quote', label: 'Save as Quote', hint: 'No commitment — review and submit later', icon: <FileTextOutlined /> },
  { value: 'order', label: 'Submit as Order', hint: 'Places the order with your ERP now', icon: <SendOutlined /> },
];

const StepSummarySubmit = ({
  requestId,
  userId,
  items,
  matches,
  selectedByItem,
  qtyByItem,
  erpPriceByProductCode,
  erpPriceLoadingByProductCode,
  headerFields,
  formValues,
  onFormValuesChange,
  checkoutFlowConfig,
  onBack,
  onSubmitted,
  pricingEngine,
  productImageBaseUrl,
  erpConnector = null,
}) => {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  // 'detailed' (Card per line, with product image) vs 'simple' (dense table, no
  // product image) -- same two views, same meaning, as StepReviewMatch.
  const [viewMode, setViewMode] = useState('detailed');

  const matchesByItemKey = useMemo(() => {
    const map = {};
    (matches || []).forEach((m) => {
      const key = String(m?.item_id ?? '');
      if (!key) return;
      (map[key] = map[key] || []).push(m);
    });
    return map;
  }, [matches]);

  const lineItems = useMemo(() => (items || []).map((item) => {
    const key = String(item.id || '');
    const itemMatches = matchesByItemKey[key] || [];
    const selectedCode = selectedByItem[key] || itemMatches[0]?.product_code;
    const selectedMatch = itemMatches.find((m) => m.product_code === selectedCode) || itemMatches[0] || null;
    const qty = Number(qtyByItem[key] ?? item?.normalized_parts?.qty ?? 1) || 1;
    const uom = String(
      selectedMatch?.match?.uom
      || item?.normalized_parts?.uom
      || 'EA'
    ).trim() || 'EA';
    const { unitPrice: price, extendedPrice: lineTotal, usingInputPriceFallback } = resolveEffectiveLinePrice({
      matchUnitPrice: selectedMatch?.pricing?.unit_price,
      erpUnitPrice: erpPriceByProductCode[selectedCode],
      inputUnitPrice: item?.normalized_parts?.input_unit_price,
      inputExtendedPrice: item?.normalized_parts?.input_extended_price,
      pricingEngine,
      qty,
    });
    // Matches resolveEffectiveLinePrice's own hasErpPrice check (Number.isFinite,
    // no >0 floor) so a genuinely $0.00 ERP price displays consistently with
    // what's actually used for `price`/`lineTotal` above, instead of "—".
    const erpUnitPriceRaw = Number(erpPriceByProductCode[selectedCode]);
    const erpUnitPrice = Number.isFinite(erpUnitPriceRaw) ? erpUnitPriceRaw : null;
    // Literal ERP unit price x qty -- distinct from `lineTotal` above, which
    // is the *resolved/effective* extended price and can come from match or
    // input-fallback pricing instead of ERP. Null (not computed from a
    // non-ERP source) when no ERP price is on file for this line.
    const erpExtendedPrice = erpUnitPrice != null ? erpUnitPrice * qty : null;
    const imageSrc = buildProductImageUrl(selectedMatch?.match?.image_url, productImageBaseUrl);
    return {
      itemId: item.id,
      selectedMatch,
      itemMatchesCount: itemMatches.length,
      imageSrc,
      // 1-based line number matching the source document's extraction order
      // (order_genie_items.row_index), not the post-filter array position.
      rowNumber: Number.isFinite(Number(item?.row_index)) ? Number(item.row_index) + 1 : null,
      partNumber: item?.normalized_parts?.part_number || '',
      description: item?.normalized_parts?.description || '',
      name: decodeHtmlEntities(selectedMatch?.match?.name || ''),
      productCode: selectedCode || '',
      matched: !!selectedMatch,
      qty,
      uom,
      price: Number.isFinite(price) ? price : null,
      lineTotal: Number.isFinite(lineTotal) ? lineTotal : null,
      erpUnitPrice,
      erpExtendedPrice,
      usingInputPriceFallback,
      // Extracted-but-otherwise-unused document fields — carried through to the
      // cart's meta_json (app-side only; not part of the ERP order-creation payload).
      manufacturer: item?.normalized_parts?.manufacturer || '',
      manufacturerPartNumber: item?.normalized_parts?.manufacturer_part_number || '',
      customerPartNumber: item?.normalized_parts?.customer_part_number || '',
      notes: item?.normalized_parts?.notes || '',
      inputUnitPrice: Number.isFinite(Number(item?.normalized_parts?.input_unit_price))
        ? Number(item.normalized_parts.input_unit_price) : null,
      inputExtendedPrice: Number.isFinite(Number(item?.normalized_parts?.input_extended_price))
        ? Number(item.normalized_parts.input_extended_price) : null,
    };
  }), [items, matchesByItemKey, selectedByItem, erpPriceByProductCode, qtyByItem, pricingEngine, productImageBaseUrl]);

  const subtotal = lineItems.reduce((sum, li) => sum + (li.lineTotal || 0), 0);
  const unmatchedLines = lineItems.filter((li) => !li.matched);

  const orderType = formValues.order_type || 'quote';
  const requiredFields = orderType === 'order'
    ? (checkoutFlowConfig?.steps?.summary_submit?.order_required_fields || ['customer', 'ship_to', 'shipping_method'])
    : (checkoutFlowConfig?.steps?.summary_submit?.quote_required_fields || ['customer']);

  const missingFields = requiredFields.filter((key) => !String(formValues[key] || '').trim());
  const blockedByUnmatched = orderType === 'order' && unmatchedLines.length > 0;
  const canSubmit = missingFields.length === 0 && !blockedByUnmatched;

  const handleSubmit = async () => {
    setError('');
    setSubmitting(true);
    try {
      const summaryPayload = {
        ...headerFields,
        customer_id_erp: formValues.customer || '',
        customerIdErp: formValues.customer || '',
        ship_to_id_erp: formValues.ship_to || '',
        contact_id: formValues.contact || '',
        shipping_method: formValues.shipping_method || '',
        order_type: orderType,
        totalItems: lineItems.length,
        selectedMatches: lineItems.filter((li) => li.matched).length,
        subtotal,
      };
      await updateOrderGenieSummary(requestId, { user_id: userId, summary: summaryPayload });
      await updateOrderGenieCart(requestId, { user_id: userId, action: 'clear', items: [] });
      const cartItems = lineItems.filter((li) => li.matched).map((li) => ({
        item_id: li.itemId,
        product_code: li.productCode,
        quantity: li.qty,
        price: li.price,
        meta: {
          manufacturer: li.manufacturer,
          manufacturer_part_number: li.manufacturerPartNumber,
          customer_part_number: li.customerPartNumber,
          notes: li.notes,
          input_unit_price: li.inputUnitPrice,
          input_extended_price: li.inputExtendedPrice,
        },
      }));
      if (cartItems.length) {
        await updateOrderGenieCart(requestId, { user_id: userId, action: 'add', items: cartItems });
      }

      let submitFormValues = formValues;
      const tokenUser = getAuthUserFromStorage();
      const shipToId = String(formValues.ship_to || '').trim();
      const customerId = String(formValues.customer || '').trim();
      const hasShipAddress = Boolean(
        String(formValues.ship_to_details?.shipToAddress?.ShipToAddress1 || '').trim()
      );
      const hasShipContactName = Boolean(
        String(formValues.ship_to_details?.ShipToContactFirstName || '').trim()
        || String(formValues.contact_details?.firstName || '').trim()
        || String(tokenUser.firstName || '').trim()
      );
      if (shipToId && customerId && (!hasShipContactName || !hasShipAddress)) {
        const resp = await searchOrderGenieErpShipToAddresses(customerId);
        const matched = (resp?.items || []).find((item) => item.shipToId === shipToId);
        if (matched) {
          submitFormValues = {
            ...formValues,
            ship_to_details: { ...(formValues.ship_to_details || {}), ...matched },
          };
        }
      }

      const customerShipTo = buildCustomerShipToPayload(
        submitFormValues,
        formValues.shipping_method,
        tokenUser,
      );

      const billDetails = formValues.bill_to_details || {};
      const customerBillTo = billDetails.CustomerBillTo || null;

      const pricedLineItems = lineItems
        .filter((li) => li.matched && Number.isFinite(Number(li.price)) && Number(li.price) > 0)
        .map((li) => ({
          ItemID: li.productCode,
          OrderQuantity: li.qty,
          UnitName: li.uom,
          UnitSize: 1,
          UnitPrice: Number(li.price),
          Description: li.name || '',
          CalculateTax: false,
          NewWebItem: false,
        }));
      if (!pricedLineItems.length) {
        throw new Error('No priced line items to submit. Remove invalid items or refresh prices.');
      }

      const erpResp = await submitOrderGenieErpOrder(requestId, {
        user_id: userId,
        order_type: orderType,
        import_as_quote: orderType === 'quote',
        customer_code: formValues.customer || '',
        po_number: headerFields.po_number || '',
        require_date: headerFields.required_date || '',
        notepad_text: headerFields.order_notes || (
          orderType === 'quote' ? 'OrderX Quote Request' : 'OrderX Order Request'
        ),
        customer_ship_to: customerShipTo,
        customer_bill_to: customerBillTo,
        shipping_method: formValues.shipping_method || '',
        contact_id: formValues.contact || '',
        line_items: pricedLineItems,
      });

      const orderNumber = erpResp?.order_number;
      const checkoutPayload = {
        requestId,
        userId,
        customerIdAsInErp: formValues.customer || '',
        shipToIdAsInErp: formValues.ship_to || '',
        shippingMethod: formValues.shipping_method || '',
        orderType,
        orderNumber,
        summary: summaryPayload,
        items: lineItems,
      };
      localStorage.setItem(
        scopedKey('order_genie_checkout_payload', getActiveAccountId()),
        JSON.stringify({ ...checkoutPayload, createdAt: new Date().toISOString() })
      );
      window.dispatchEvent(new CustomEvent('order_genie_checkout_ready', { detail: checkoutPayload }));
      onSubmitted({ requestId, orderType, orderNumber });
    } catch (err) {
      setError(err?.message || 'Unable to submit.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="row g-4">
      <div className="col-12 col-lg-8">
        <Card className="shadow-sm border-0 mb-3">
          <h5 className="mb-3">Request Details</h5>
          <div className="row g-2 small">
            {headerFields.po_number && <div className="col-6"><strong>PO#:</strong> {headerFields.po_number}</div>}
            {headerFields.required_date && <div className="col-6"><strong>Required:</strong> {headerFields.required_date}</div>}
            {headerFields.project_name && <div className="col-6"><strong>Project:</strong> {headerFields.project_name}</div>}
            {headerFields.order_notes && <div className="col-12"><strong>Notes:</strong> {headerFields.order_notes}</div>}
          </div>
        </Card>

        <Card className="shadow-sm border-0 mb-3">
          <h5 className="mb-3">Customer &amp; Shipping</h5>
          <CustomerShipToContactCards formValues={formValues} className="mb-2" />
          <div className="small">
            {formValues.bill_to_details?.billingAddress && (
              <div><strong>Bill To:</strong> {formValues.bill_to_details.billingAddress}</div>
            )}
            <div><strong>Shipping Method:</strong> {getShippingMethodLabel(formValues.shipping_method, erpConnector) || <span className="text-muted">Not set</span>}</div>
          </div>
        </Card>

        <div className="d-flex justify-content-end mb-2">
          <Segmented
            size="small"
            value={viewMode}
            onChange={setViewMode}
            aria-label="Line items view"
            options={[
              { value: 'detailed', icon: <AppstoreOutlined />, label: 'Detailed' },
              { value: 'simple', icon: <BarsOutlined />, label: 'Simple' },
            ]}
          />
        </div>

        <Card className="shadow-sm border-0">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h5 className="mb-0">Line Items</h5>
            <Button
              size="small"
              icon={<DownloadOutlined />}
              onClick={() => exportReviewToExcel({
                items, matches, selectedByItem, qtyByItem, erpPriceByProductCode, pricingEngine,
                fileNamePrefix: 'orderx_summary_submit',
              })}
            >
              Export to Excel
            </Button>
          </div>
          {viewMode === 'simple' && (
            <div className="table-responsive">
              <table className="table table-sm table-bordered align-middle mb-0 bg-white" style={{ tableLayout: 'fixed' }}>
                <colgroup>
                  <col style={{ width: 36 }} />
                  <col style={{ width: '34%' }} />
                  <col style={{ width: '34%' }} />
                  <col style={{ width: 70 }} />
                  <col style={{ width: 130 }} />
                </colgroup>
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Extracted from Document</th>
                    <th>Matched Product</th>
                    <th className="text-end">Qty</th>
                    <th className="text-end">Price</th>
                  </tr>
                </thead>
                <tbody>
                  {lineItems.map((li, idx) => (
                    <tr key={li.itemId} className={!li.matched ? 'table-danger' : undefined}>
                      <td className="text-muted small">{li.rowNumber ?? idx + 1}</td>
                      <td>
                        <SimpleExtractedFields
                          partNumber={li.partNumber}
                          manufacturer={li.manufacturer}
                          manufacturerPartNumber={li.manufacturerPartNumber}
                          customerPartNumber={li.customerPartNumber}
                          description={li.description}
                          notes={li.notes}
                        />
                      </td>
                      <td>
                        {li.matched ? (
                          <>
                            <div className="fw-semibold small">
                              {li.productCode}
                              {li.itemMatchesCount > 1 ? ` (${li.itemMatchesCount} options)` : ''}
                            </div>
                            <div
                              className="text-muted small"
                              title={li.name}
                              style={{
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden',
                              }}
                            >
                              {li.name}
                            </div>
                          </>
                        ) : (
                          <Tag color="red">No match</Tag>
                        )}
                      </td>
                      <td className="text-end">{li.qty}</td>
                      <td className="text-end small">
                        {/* Same From Doc block, then ERP block ordering (unit price
                            before extended, within each) as PricingPanel's Detailed
                            view below -- see LineItemPriceDisplay.jsx's PricingPanel. */}
                        {li.inputUnitPrice != null && (
                          <>
                            <div className="text-muted" style={{ fontSize: 11 }}>From Doc</div>
                            <div>{`$${li.inputUnitPrice.toFixed(2)}/ea`}</div>
                            <div className="text-muted mb-1" style={{ fontSize: 11 }}>
                              {`Ext: ${li.inputExtendedPrice != null ? `$${li.inputExtendedPrice.toFixed(2)}` : '—'}`}
                            </div>
                          </>
                        )}
                        <div className="text-muted" style={{ fontSize: 11 }}>ERP</div>
                        <div>{li.erpUnitPrice != null ? `$${li.erpUnitPrice.toFixed(2)}/ea` : '—'}</div>
                        <div className="fw-semibold" style={{ color: '#0f6b3f' }}>
                          {`Ext: ${li.erpExtendedPrice != null ? `$${li.erpExtendedPrice.toFixed(2)}` : '—'}`}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {viewMode === 'detailed' && lineItems.length === 0 && (
            <div className="text-muted small">No line items.</div>
          )}

          {viewMode === 'detailed' && lineItems.map((li, idx) => {
            const { selectedMatch, itemMatchesCount } = li;
            const priceLoading = Boolean(li.productCode && erpPriceLoadingByProductCode?.[li.productCode]);
            return (
              <Card key={li.itemId ?? idx} className={`shadow-sm border-0 mb-3 ${!li.matched ? 'border border-danger' : ''}`}>
                <div className="mb-3">
                  <ExtractedFieldsBlock
                    partNumber={li.partNumber}
                    manufacturer={li.manufacturer}
                    manufacturerPartNumber={li.manufacturerPartNumber}
                    customerPartNumber={li.customerPartNumber}
                    description={li.description}
                    notes={li.notes}
                    headerPrefix={(
                      <>
                        <Tag color="blue">LINE {li.rowNumber ?? idx + 1}</Tag>
                        {!li.matched && <Tag color="red">No match</Tag>}
                      </>
                    )}
                  />
                </div>

                <div className="d-flex gap-3 flex-wrap flex-md-nowrap">
                  <ProductImage src={li.imageSrc} alt={selectedMatch?.match?.name || ''} />

                  <div className="flex-grow-1" style={{ minWidth: 200, ...(selectedMatch ? CARD_STYLES.matched : CARD_STYLES.unmatched) }}>
                    <div className="text-muted small fw-semibold mb-1" style={{ letterSpacing: 0.3 }}>
                      Matched Product
                    </div>
                    {selectedMatch ? (
                      <>
                        <div className="fw-semibold">
                          {li.productCode}
                          {itemMatchesCount > 1 ? ` (${itemMatchesCount} options found)` : ''}
                        </div>
                        <div className="text-muted small">{li.name}</div>
                        {(() => {
                          const aiBadge = getMatchAiBadge(selectedMatch);
                          const uomMismatchBadge = getUomMismatchBadge(selectedMatch);
                          const erpBadge = getErpMatchBadge(selectedMatch);
                          const priceDivergenceBadge = getPriceDivergenceBadge(li.inputUnitPrice, li.price);
                          if (!selectedMatch.reasoning && !aiBadge && !uomMismatchBadge && !erpBadge && !priceDivergenceBadge) return null;
                          return (
                            <div className="text-muted small mt-1">
                              {aiBadge && <Tag color={aiBadge.color} className="me-1">{aiBadge.label}</Tag>}
                              {uomMismatchBadge && (
                                <Tag color={uomMismatchBadge.color} className="me-1">{uomMismatchBadge.label}</Tag>
                              )}
                              {erpBadge && <Tag color={erpBadge.color} className="me-1">{erpBadge.label}</Tag>}
                              {priceDivergenceBadge && (
                                <Tag color={priceDivergenceBadge.color} className="me-1">{priceDivergenceBadge.label}</Tag>
                              )}
                              {selectedMatch.reasoning && (
                                <>
                                  <span className="fw-semibold">Match reason: </span>
                                  {decodeHtmlEntities(selectedMatch.reasoning)}
                                </>
                              )}
                            </div>
                          );
                        })()}
                      </>
                    ) : (
                      <div className="text-muted small">No catalog match found for this line.</div>
                    )}
                  </div>

                  <PricingPanel
                    unitPrice={li.price}
                    uom={li.uom}
                    qty={li.qty}
                    readOnly
                    extendedPrice={li.lineTotal}
                    inputUnitPrice={li.inputUnitPrice}
                    inputExtendedPrice={li.inputExtendedPrice}
                    usingInputPriceFallback={li.usingInputPriceFallback}
                    priceLoading={priceLoading}
                  />
                </div>
              </Card>
            );
          })}
        </Card>
      </div>

      <div className="col-12 col-lg-4">
        <Card className="shadow-sm border-0" style={{ position: 'sticky', top: 16 }}>
          <h5 className="mb-3">Total</h5>
          <div className="d-flex justify-content-between fw-semibold fs-5 mb-1">
            <span>Subtotal</span>
            <span>${subtotal.toFixed(2)}</span>
          </div>
          <div className="text-muted small mb-3">Shipping &amp; tax calculated separately</div>

          <div className="mb-3">
            <div className="text-muted small fw-semibold mb-2" style={{ letterSpacing: 0.3 }}>
              HOW WOULD YOU LIKE TO PROCEED?
            </div>
            <Segmented
              block
              size="large"
              disabled={submitting}
              value={orderType}
              onChange={(val) => onFormValuesChange({ order_type: val })}
              aria-label="How would you like to proceed"
              options={ORDER_TYPE_TOGGLE_OPTIONS.map((opt) => ({
                value: opt.value,
                label: (
                  <span className="d-flex align-items-center justify-content-center gap-1 py-1">
                    {opt.icon}
                    {opt.label}
                  </span>
                ),
              }))}
            />
            <div className="text-muted small mt-2">
              {ORDER_TYPE_TOGGLE_OPTIONS.find((opt) => opt.value === orderType)?.hint}
            </div>
          </div>

          {missingFields.length > 0 && (
            <div className="alert alert-warning py-2 small">
              Missing: {missingFields.map((k) => FIELD_LABELS[k] || k).join(', ')}
            </div>
          )}
          {blockedByUnmatched && (
            <div className="alert alert-warning py-2 small">
              {unmatchedLines.length} line{unmatchedLines.length === 1 ? '' : 's'} unmatched — resolve or switch to Quote.
            </div>
          )}
          {error && <div className="alert alert-danger py-2 small">{error}</div>}

          <Button
            type="primary"
            block
            size="large"
            icon={!submitting && (orderType === 'order' ? <SendOutlined /> : <FileTextOutlined />)}
            loading={submitting}
            disabled={!canSubmit}
            onClick={handleSubmit}
            className="mt-1"
          >
            {submitting ? 'Submitting…' : orderType === 'order' ? 'Submit Order' : 'Save Quote'}
          </Button>
          <Button type="text" block className="mt-2" onClick={onBack} disabled={submitting}>
            &larr; Back to Customer &amp; Shipping
          </Button>
        </Card>
      </div>
    </div>
  );
};

export default StepSummarySubmit;
